import className from "classnames";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { isIOS } from "PFCore/helpers/ios";
import { isSafari } from "PFCore/helpers/is_safari";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { downloadAttachment } from "PFCore/services/attachments/download_attachment";
import { Attachment } from "PFCore/types";
import { Timeout } from "PFTypes/timeout";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./download_link.module.scss";

type DownloadLinkProps = {
  attachment: Attachment;
  useIcon?: boolean;
  darkBackground?: boolean;
  rootClassName?: string;
};

const DownloadLink = ({ attachment, useIcon = true, darkBackground, rootClassName }: DownloadLinkProps) => {
  const { t } = useTranslation(["core", "translation"]);
  const [loading, setLoading] = useState(false);
  const [fileUrl, setFileUrl] = useState("");
  const timeoutRef = useRef<Timeout>(null);

  const { showGrowl } = useGrowlStoreActions();
  const { getFirstError } = useResponseErrors();

  const useAnchorTag = isIOS() || isSafari();

  const handleClick = (e) => {
    window.clearTimeout(Number(timeoutRef.current));

    e.stopPropagation();

    setLoading(true);

    // Because of business reasons we do not get the url to the resource,
    // but /api/attachments/:id/download enpoint that provides it.
    // The response is out-of-date in short time.
    downloadAttachment({ id: attachment.id }).then(
      (resp) => {
        if (useAnchorTag) {
          setFileUrl(resp.fileUrl);
          setLoading(false);
          timeoutRef.current = setTimeout(() => {
            setFileUrl("");
          }, 10 * 1000);
        } else {
          const iframe = document.createElement("iframe");
          iframe.style.display = "none";
          iframe.setAttribute("src", resp.fileUrl);
          document.body.appendChild(iframe);
        }
      },
      (error) => {
        const errorMessage = getFirstError(error);

        showGrowl({
          kind: "error",
          title: errorMessage,
          message: t("core:fileCannotBeOpen")
        });
      }
    );
  };

  return (
    <div>
      <Button
        kind="text"
        onClick={handleClick}
        className={className(css.link, { [css.darkBackground]: darkBackground }, rootClassName)}
        icon={useIcon ? "export" : undefined}
        text={attachment.fileName}
      />
      {useAnchorTag && (
        <div className={css.link2}>
          {loading && " ↳ ..."}
          {!loading && fileUrl && (
            <React.Fragment>
              {" ↳ "}
              <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                {t("translation:download")}
              </a>
            </React.Fragment>
          )}
        </div>
      )}
    </div>
  );
};

export default DownloadLink;
